// Green Screen Yapper Ad's form (live's `green-screen-yapper-ad-form/library-view` at 8255e2a, spec
// G14-green-screen-yapper, PR h; no capture, GSY-4): live's staged form in two approvals. The
// script stage picks the product (one of the brand's, or an uploaded packshot) and the creator (a
// person from the library or a photo, or none for the director's design), writes the script (the
// server's director, or the member) and approves it; the images stage shows the five stills and the
// card read back (`GreenScreenImages.tsx`). The draft is the server's (`green-screen-draft.ts`).
// "Generate final video" starts the run (`green_screen_yapper_ad.py`); after a run whose video model
// refused a talking clip, Revise opens that clip's lines and "Approve and generate final video"
// starts the next run, which keeps the other clips' takes (PR f4).
import { Group, Loader, Stack, Text, TextInput, Tooltip } from "@mantine/core";
import { Clapperboard, Layers, Monitor, Repeat, Smartphone } from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";

import type { Job } from "../../../api/generated/model";
import {
  VideoTemplateQuote,
  VideoTemplateRequest,
  greenScreenInputsCategoryMax,
  greenScreenInputsCreatorLookMax,
  greenScreenInputsPronunciationMax,
  greenScreenInputsSloganMax,
  type GreenScreenInputsOutput,
} from "../../../api/generated/zod";
import { ErrorAlert } from "../../../platform/ErrorAlert";
import { usePricedStart } from "../../../platform/jobs/priced-start";
import type { PickedActor } from "../../../platform/actors/picked";
import { Choice } from "../../../ui/Choice";
import { FieldLabel } from "../../../ui/FieldLabel";
import { GlassPanel } from "../../../ui/GlassPanel";
import { GreenScreenImages } from "./GreenScreenImages";
import { GreenScreenScriptSection } from "./GreenScreenScript";
import { InputChip } from "./PodcastScript";
import { CastTile, sourceKey, type PickedPictures } from "./PodcastTiles";
import { StreetProductTile, type ProductWords } from "./StreetTiles";
import { ReuseSwitch, type TemplateFormProps } from "./kit";
import { Submit, Subtitles } from "./staged";
import {
  currentTakes,
  drawnStills,
  pausedRun,
  refusedClips,
  refusedFlags,
  revisedClips,
  useGreenScreenDraft,
  type DraftWork,
} from "./green-screen-draft";
import classes from "./podcast.module.css";

/** Live's words (`library-view/constants.ts`). */
const PRODUCT_WORDS: ProductWords = {
  tile:
    "The product the creator talks about. Every claim is quoted from its page text; the packshot is " +
    "only read, never shown.",
  name: "The product name the script uses. Leave empty and the director reads it off the packshot.",
  text: "A description or the product page. Every claim in the script is quoted from it.",
};
const CREATOR_TOOLTIP =
  "Who the creator is. By default the script director designs a young woman for the product's buyer. " +
  "You can also pick a person from the created.ai avatar library or upload a photo: the voice then " +
  "follows their gender and age, and they keep their own clothes.";
const CREATOR_LOOK_TOOLTIP =
  "A description of the creator's look. Empty = the director designs her for the buyer. The t-shirt, " +
  "framing and voice are fixed by the format.";
const SUBTITLES_TOOLTIP =
  "Karaoke captions of the creator's words (white, the spoken word in yellow) in the lower part, under " +
  "the card. The card and the captions are the only text on the video.";
const VIDEO_MODEL_TOOLTIP =
  "One video model for the whole ad. Omni Flash 1.1 is the default; Seedance 2.0 is the alternative.";
const RESOLUTION_TOOLTIP = "Output resolution: 720p or 1080p.";
const TAKES_TOOLTIP = "Takes per clip (1–3); the best one is picked by the word and picture check.";
const COMPOSITE_TOOLTIP =
  "Key: green backdrop keyed out (the format's look). Inset: white backdrop, no keying, the creator's " +
  "frame laid into the lower part of the card (the fallback).";
const FORMAT_TOOLTIP = "Every frame and clip is vertical 9:16.";
const APPROVE_SCRIPT = "Approve script";
const GENERATE_VIDEO = "Generate final video";
const PICK_PRODUCT = "Pick a product and its packshot";
const GENERATE_SCRIPT = "Generate the script";
const STILLS_PENDING = "Wait for every image";
const HANDOFF_NOTICE =
  "Your green screen yapper ad is finishing in My Generations — you can close this panel now.";
const VIDEO_MODELS = [
  { value: "gemini-omni-flash-1.1", label: "Omni Flash 1.1" },
  { value: "seedance-2-standard", label: "Seedance 2.0" },
] as const;
const RESOLUTIONS = ["1080p", "720p"] as const;
const TAKES = [
  { value: "1", label: "1 take" },
  { value: "2", label: "2 takes" },
  { value: "3", label: "3 takes" },
] as const;
const ROUTES = [
  { value: "key", label: "Key" },
  { value: "inset", label: "Inset" },
] as const;
const CHIPS = [
  {
    field: "category",
    label: "Category",
    placeholder: "pimple patch (optional)",
    tooltip:
      'The category as people say it; it fills the hook ("Every pimple patch worth buying right now does ' +
      'the same five things.") and the card title. Empty = from the product page.',
    maxLength: greenScreenInputsCategoryMax,
  },
  {
    field: "slogan",
    label: "Slogan",
    placeholder: "Don't pop it. Patch it. (optional)",
    tooltip: "The brand's own slogan, the ad's last line. Empty = found on the product page.",
    maxLength: greenScreenInputsSloganMax,
  },
  {
    field: "pronunciation",
    label: "Pronunciation",
    placeholder: "YOO-zoo (optional)",
    tooltip: "How the brand is said. Empty = the director adds a respelling for any word likely to be misread.",
    maxLength: greenScreenInputsPronunciationMax,
  },
] as const;

/** The video's settings live's footer keeps open at the images (all but the route, fixed at
 * approval with the stills it draws): the form's own until "Generate final video" sends them. */
type VideoSettings = Pick<
  GreenScreenInputsOutput,
  "subtitles" | "caption_position" | "caption_style_id" | "video_model" | "resolution" | "take_count"
>;

/** Live's label while revising (`APPROVE_REVISION_LABEL`). */
const APPROVE_REVISION = "Approve and generate final video";

/** Live's progress line under the form (`PROGRESS_LABELS`). */
function progressOf(work: DraftWork, drawn: number, total: number): string | null {
  if (work.writing) return "Writing the script";
  if (work.checking) return "Reading the card back";
  return work.drawing ? `Generating images ${drawn}/${total}` : null;
}

/**
 * Live's form: "Reuse example" (off and disabled until the card's example is staged), the stage,
 * and in the footer the subtitles, the clip model, the resolution, the takes, the route and the
 * 9:16 format, then the round start: "Approve script", then "Generate final video", whose
 * tooltip says what it waits for. The model, the resolution, the takes and the subtitles stay
 * editable at the images, as live's; the route does not. After a run that ended without its video,
 * they open as that run had them, so its unchanged clips are kept; while revising, the form is the
 * approved script with only the refused clips open, and its submit approves and starts the run.
 */
export function GreenScreenAdForm(props: TemplateFormProps) {
  const { teamId, brandId, above, onStarted, example } = props;
  const [collapsed, setCollapsed] = useState(false);
  const flow = useGreenScreenDraft(teamId, brandId);
  const [pictures, setPictures] = useState<PickedPictures>({});
  const [productAsked, setProductAsked] = useState(false);
  const [settings, setSettings] = useState<VideoSettings | null>(null);
  const [started, setStarted] = useState<string | null>(null);
  const [startAfterApproval, setStartAfterApproval] = useState(false);
  const fired = useRef(false);
  const { form, draft } = flow;
  const approved = draft !== undefined && draft.stage !== "script";
  const revised = draft === undefined ? [] : revisedClips(draft);
  const keepsSettings = approved || revised.length > 0;
  const paused = draft === undefined ? null : pausedRun(draft);
  // Only while the draft still holds that run's images: elsewhere the form's own settings count.
  const was = keepsSettings ? paused?.inputs : undefined;
  const shown: VideoSettings | null =
    form === null
      ? null
      : ((keepsSettings ? settings : null) ?? {
          subtitles: was?.subtitles ?? form.inputs.subtitles,
          caption_position: was?.caption_position ?? form.inputs.caption_position,
          caption_style_id: was?.caption_style_id ?? form.inputs.caption_style_id,
          video_model: was?.video_model ?? form.inputs.video_model,
          resolution: was?.resolution ?? form.inputs.resolution,
          take_count: was?.take_count ?? form.inputs.take_count,
        });
  const stills = draft === undefined ? null : drawnStills(draft);
  const next =
    draft === undefined || form === null || shown === null || stills === null || draft.script === null
      ? null
      : {
          template: "green-screen-yapper-ad" as const,
          script: draft.script,
          stills,
          photo_creator: form.inputs.creator !== undefined && form.inputs.creator !== null,
          composite_route: form.inputs.composite_route,
          ...shown,
        };
  const takes = draft === undefined || next === null ? {} : currentTakes(draft, next);
  const video =
    next === null
      ? null
      : {
          type: "video.template" as const,
          inputs: { ...next, ...(Object.keys(takes).length === 0 ? {} : { takes }) },
        };
  const videoRequest = VideoTemplateRequest.safeParse(video);
  const videoQuote = VideoTemplateQuote.safeParse(video);
  const videoStart = usePricedStart({
    teamId,
    brandId,
    quote: videoQuote.success ? videoQuote.data : undefined,
    request: videoRequest.success ? videoRequest.data : undefined,
    onStarted: (job: Job) => {
      setStartAfterApproval(false);
      setStarted(job.id);
      onStarted(job);
    },
  });
  if (!keepsSettings && settings !== null) setSettings(null);
  // "Approve and generate final video": the run starts once, when the approved draft is priced. A
  // refused quote or start, or an approved draft that cannot start (a still missing), ends the wait
  // for good, shown as any other refusal: what is paid for later waits for the member's own click.
  const { canStart, startJob } = videoStart;
  const startRefused =
    videoStart.quoteError !== null || videoStart.start.isError || (approved && !videoRequest.success);
  if (startAfterApproval && startRefused) setStartAfterApproval(false);
  const awaitingStart = startAfterApproval && !startRefused;
  useEffect(() => {
    if (!awaitingStart || !canStart || fired.current) return;
    fired.current = true;
    startJob();
  }, [awaitingStart, canStart, startJob]);

  const panel = (body: ReactNode, footer?: ReactNode) => (
    <GlassPanel
      collapsed={collapsed}
      onCollapsedChange={setCollapsed}
      above={above}
      header={<ReuseSwitch example={null} checked={false} onChange={() => undefined} />}
      summary={<Text size="sm">Green Screen Yapper Ad</Text>}
      footer={footer}
    >
      {body}
    </GlassPanel>
  );
  if (flow.read.isError) return panel(<ErrorAlert error={flow.read.error} />);
  if (draft === undefined || form === null || shown === null) {
    return panel(<Loader size="sm" aria-label="Loading the draft" />);
  }

  const { inputs, script } = form;
  const setInputs = (change: Partial<GreenScreenInputsOutput>) => flow.editInputs(change);
  const changeSettings = (change: Partial<VideoSettings>) => {
    if (keepsSettings) setSettings({ ...shown, ...change });
    else setInputs(change);
  };
  const pick = (actor: PickedActor) => {
    setPictures((current) => ({ ...current, [sourceKey(actor.source)]: { url: actor.imageUrl, name: actor.name } }));
    setInputs({ creator: actor.source });
  };
  const revisingNow = revised.length > 0;
  // While revising, the picks and the route are kept with the images.
  const locked = !flow.editable || revisingNow;
  const busy = flow.busy || videoStart.start.isPending;
  const work = flow.work;
  const drawn = draft.stills.filter((each) => each.status === "done").length;
  const progress = progressOf(work, drawn, draft.stills.length);

  let submit;
  if (revisingNow) {
    submit = (
      <Submit
        label={APPROVE_REVISION}
        reasons={[]}
        disabled={script === null || draft.script_issue !== null || busy || awaitingStart}
        loading={flow.approving || awaitingStart}
        onClick={() =>
          flow.approveRevision(() => {
            fired.current = false;
            videoStart.start.reset();
            setStartAfterApproval(true);
          })
        }
      />
    );
  } else if (approved) {
    const ready = stills !== null;
    submit = (
      <Submit
        label={GENERATE_VIDEO}
        reasons={ready ? [] : [STILLS_PENDING]}
        disabled={!ready || busy || !videoStart.canStart}
        loading={videoStart.start.isPending}
        onClick={videoStart.startJob}
      />
    );
  } else {
    const reasons = [...(inputs.product ? [] : [PICK_PRODUCT]), ...(script === null ? [GENERATE_SCRIPT] : [])];
    submit = (
      <Submit
        label={APPROVE_SCRIPT}
        reasons={reasons}
        disabled={reasons.length > 0 || draft.script_issue !== null || busy}
        loading={flow.approving}
        onClick={flow.approve}
      />
    );
  }

  const footer = (
    <Group justify="space-between" wrap="nowrap" gap={12}>
      <div className={classes.settings}>
        <Subtitles
          on={shown.subtitles}
          position={shown.caption_position}
          disabled={busy}
          tooltip={SUBTITLES_TOOLTIP}
          onChange={(change) => changeSettings(change)}
        />
        <Choice
          variant="footer"
          setting="Video model"
          icon={<Clapperboard size={16} strokeWidth={1.5} />}
          tip={VIDEO_MODEL_TOOLTIP}
          width={136}
          value={shown.video_model}
          options={VIDEO_MODELS.map((each) => ({ ...each }))}
          disabled={busy}
          onChange={(next) => {
            const model = VIDEO_MODELS.find((each) => each.value === next);
            if (model !== undefined) changeSettings({ video_model: model.value });
          }}
        />
        <Choice
          variant="footer"
          setting="Resolution"
          icon={<Monitor size={16} strokeWidth={1.5} />}
          tip={RESOLUTION_TOOLTIP}
          width={88}
          value={shown.resolution}
          options={RESOLUTIONS.map((value) => ({ value, label: value }))}
          disabled={busy}
          onChange={(next) => {
            const resolution = RESOLUTIONS.find((each) => each === next);
            if (resolution !== undefined) changeSettings({ resolution });
          }}
        />
        <Choice
          variant="footer"
          setting="Takes"
          icon={<Repeat size={16} strokeWidth={1.5} />}
          tip={TAKES_TOOLTIP}
          width={96}
          value={String(shown.take_count)}
          options={TAKES.map((each) => ({ ...each }))}
          disabled={busy}
          onChange={(next) => {
            const takes = Number(next);
            if (takes >= 1 && takes <= 3) changeSettings({ take_count: takes });
          }}
        />
        <Choice
          variant="footer"
          setting="Composite"
          icon={<Layers size={16} strokeWidth={1.5} />}
          tip={COMPOSITE_TOOLTIP}
          width={80}
          value={inputs.composite_route}
          options={ROUTES.map((each) => ({ ...each }))}
          disabled={locked}
          onChange={(next) => {
            const route = ROUTES.find((each) => each.value === next);
            if (route !== undefined) setInputs({ composite_route: route.value });
          }}
        />
        <Tooltip label={FORMAT_TOOLTIP} multiline maw={280} withArrow>
          <span className={classes.setting}>
            <Smartphone size={16} strokeWidth={1.5} aria-hidden />
            9:16
          </span>
        </Tooltip>
      </div>
      {submit}
    </Group>
  );

  const refused = videoStart.start.error ?? videoStart.quoteError ?? flow.failure;
  const status = (
    <>
      {draft.problem === null ? null : (
        <Text className={classes.problem} role="alert">
          {draft.problem}
        </Text>
      )}
      {refused === null ? null : <ErrorAlert error={refused} />}
      {progress === null ? null : (
        <output className={classes.progress}>
          <Loader size={14} aria-hidden />
          {progress}
        </output>
      )}
      {started !== null && approved ? (
        <Text component="output" size="sm">
          Generation started. {HANDOFF_NOTICE}
        </Text>
      ) : null}
    </>
  );
  if (approved && draft.script !== null) {
    return panel(
      <div className={classes.content}>
        <GreenScreenImages
          script={draft.script}
          plan={draft.plan}
          stills={draft.stills}
          cardCheck={draft.card_check}
          checking={draft.card_checking}
          busy={busy}
          resetting={flow.resetting}
          flags={refusedFlags(draft)}
          canRevise={refusedClips(draft).length > 0}
          onRegenerate={flow.regenerate}
          onBack={flow.backToScript}
          onRevise={flow.reviseClips}
          onStartOver={() => {
            setStarted(null);
            flow.startOver();
          }}
        />
        {status}
      </div>,
      footer,
    );
  }

  const person = inputs.creator ?? null;
  const body = (
    <div className={classes.content}>
      <Stack gap={12}>
        <div className={classes.tiles}>
          <StreetProductTile
            teamId={teamId}
            brandId={brandId}
            pick={inputs.product ?? null}
            typed={{ product_name: inputs.product_name, product_text: inputs.product_text }}
            disabled={locked}
            missing={productAsked}
            exampleUrls={example?.urls ?? {}}
            words={PRODUCT_WORDS}
            onChange={(product, typed) => setInputs({ product, ...typed })}
          />
          <CastTile
            teamId={teamId}
            brandId={brandId}
            label="Creator"
            tooltip={CREATOR_TOOLTIP}
            source={person}
            making={null}
            pictures={pictures}
            disabled={locked}
            onMakeActor={null}
            onPick={pick}
            onRemove={() => setInputs({ creator: null })}
          />
        </div>
        <div className={classes.chips}>
          {CHIPS.map((chip) => (
            <InputChip
              key={chip.field}
              label={chip.label}
              value={inputs[chip.field]}
              placeholder={chip.placeholder}
              tooltip={chip.tooltip}
              maxLength={chip.maxLength}
              disabled={locked}
              onChange={(value) => {
                const change: Partial<GreenScreenInputsOutput> = {};
                change[chip.field] = value;
                setInputs(change);
              }}
            />
          ))}
        </div>
        {person === null ? (
          <TextInput
            label={<FieldLabel label="Creator look" info={CREATOR_LOOK_TOOLTIP} />}
            placeholder="Age 20–25, face, hair, skin (optional)"
            maxLength={greenScreenInputsCreatorLookMax}
            value={inputs.creator_look}
            disabled={locked}
            onChange={(event) => setInputs({ creator_look: event.currentTarget.value })}
          />
        ) : null}
        <GreenScreenScriptSection
          script={script}
          plan={draft.plan}
          photo={person !== null}
          writing={work.writing}
          locked={!flow.editable}
          issue={draft.script_issue}
          problems={draft.script_problems}
          revised={revised}
          onGenerate={() => {
            if (!inputs.product) {
              setProductAsked(true);
              return;
            }
            flow.generateScript();
          }}
          onChange={(next) => flow.edit({ script: next })}
        />
      </Stack>
      {status}
    </div>
  );
  return panel(body, footer);
}
